// Single source of truth for staged-rollout features. Flipping a flag to
// `true` opens the feature to all users. Allowlisted users (see
// constants/addons.js → ADMIN_BETA_ALLOWLIST) always bypass the flag so
// admins can keep training the feature against the live database.
//
// To open a feature post-launch:
//   1. Flip its flag here to `true`
//   2. Drop the `comingSoon: true` from constants/navigation.js if present
//   3. Ship and announce
import { isAllowedBetaUser } from "../constants/addons";
import { isNative, nativeAppVersion } from "../../utils/platform";

export const FILM_STUDIO_OPENS = "2026-10-12T13:00:00Z"; // Mon Oct 12, 9:00 AM ET

export const FEATURES = {
 askbam: false, // AI assistant: still being trained
 // Coach map: opened 2026-10-10 (Coleman: "we have enough members"). Needs
 // supabase/migrations/2026-10-10_map_visibility.sql (applied 2026-10-10), so the map
 // honours "Show me on the community map".
 memberMap: true,
 // Film Studio opens to everyone on the web at FILM_STUDIO_OPENS (Coleman,
 // 2026-10-10: "open it next week"), with a pop-up (featureIntros.js).
 // Still hidden in the iPhone app, as before.
 filmStudio: Date.now() >= Date.parse(FILM_STUDIO_OPENS),
 devStudio: true, // My Players: open to everyone
 constraints: false, // Constraint Library + saved constraints: parked, not cut
 howICoach: false, // How We Play: building it in the open, allowlist-only
 // Learning Lab and Foundations. One flag for both because they're one
 // Open (2026-09-28). What was holding it shut was the flashcard lane: it
 // served Foundations, which were never published, so the Lab would have
 // opened onto a dead lane on day one. That lane is Concepts now and has
 // real content behind it.
 //
 // NOTE: this only unlocks the client. The Lab's RPCs are gated server-side
 // by lab_gate(). Opening this flag without also opening that function gets
 // a coach a page full of permission errors. Both, or neither:
 // supabase/migrations/2026-09-28_lab_open.sql is the other half.
 learningLab: true,
 // One-to-one direct messages. On since 2026-10-10, when Coleman ran
 // supabase/migrations/2026-10-09_direct_messages.sql.
 directMessages: true,
 // BAM IQ: not a flag. It's open to the exact accounts on its list (see
 // bamiqAvailable below), never to everyone by flipping a switch here.
 // The AFS pilot (#/afs, narration): not a flag either. Open to the accounts
 // on the server's list (AFS_PILOT_USER_IDS plus the founder), asked through
 // /api/afs-narrate. `afsPilot` stays unset so the tour never offers it.
};

/* DMs in the iPhone app only from this App Store version on. A new
   private-messaging surface is Apple review territory (CLAUDE.md guardrail
   B, Guideline 1.2), so it must reach the app in a reviewed build, never by
   OTA. Gating on the native version does that: an OTA to the 1.0 app keeps
   DMs off, and the build Apple reviews (1.1) has them on. Bump the app's
   Version to this in Xcode for that release. */
export const DM_MIN_NATIVE_VERSION = "1.1";

/* The Intern Hub in the iPhone app only from this App Store version on, so a
   new feature area reaches the app in a build Apple reviews (CLAUDE.md
   guardrail B), not by OTA. On the web it's on now (Coleman, 2026-10-13:
   "we could just do it on the browser"). Interns log hours from their phone's
   browser until then. */
export const INTERN_HUB_MIN_NATIVE_VERSION = "1.1";
export function internHubAvailable() {
  return !isNative() || versionAtLeast(nativeAppVersion(), INTERN_HUB_MIN_NATIVE_VERSION);
}

/* Film Desk (the shared clip library) in the iPhone app only from a reviewed
   build (CLAUDE.md guardrail B: a new feature area with uploads reaches the app
   through review, not OTA). On the web it's on now. The narration mic also uses
   the browser Web Speech API, absent in the iOS WebView, so voice is web only;
   typing is the universal path either way. */
export const FILMDESK_MIN_NATIVE_VERSION = "1.1";
export function filmdeskAvailable() {
  return !isNative() || versionAtLeast(nativeAppVersion(), FILMDESK_MIN_NATIVE_VERSION);
}

/* BAM IQ in the iPhone app only from a reviewed App Store build (D106; CLAUDE.md
   guardrail B: a materially new AI feature never arrives by OTA). null means
   no reviewed build has it yet, so the app never shows it. The server checks
   the same thing (api/_bamiq/gate.js, BAMIQ_MIN_NATIVE_VERSION). */
export const BAMIQ_MIN_NATIVE_VERSION = null;

/* Who sees BAM IQ (D145, the private alpha): the signed-in accounts whose
   Supabase user id is in REACT_APP_BAMIQ_USER_IDS. Only whether to draw it:
   the server decides access on its own list (BAMIQ_ALLOWED_USER_IDS), so a
   browser that shows the button anyway gets nothing. Unset: nobody. Not
   staff status, not email. */
export function bamiqUserIds(env = process.env) {
  return String(env.REACT_APP_BAMIQ_USER_IDS || "").split(",").map(s => s.trim().toLowerCase()).filter(Boolean);
}
/* Which players' plans BAM IQ may be given (D146): only the ids in
   REACT_APP_BAMIQ_PLAYER_IDS (a synthetic test player during the alpha).
   Empty: no plan can be attached. The server checks its own list too. */
export function bamiqPlayerIds(env = process.env) {
  return String(env.REACT_APP_BAMIQ_PLAYER_IDS || "").split(",").map(s => s.trim().toLowerCase()).filter(Boolean);
}
export function bamiqPlayerAllowed(playerId, env = process.env) {
  return !!playerId && bamiqPlayerIds(env).includes(String(playerId).toLowerCase());
}

export function bamiqAvailable(userId, env = process.env) {
  if (!userId || !bamiqUserIds(env).includes(String(userId).toLowerCase())) return false;
  return !isNative() || (BAMIQ_MIN_NATIVE_VERSION != null && versionAtLeast(nativeAppVersion(), BAMIQ_MIN_NATIVE_VERSION));
}

/* Team rosters in the iPhone app (Coleman, 2026-10-23: "Web first, iOS in next App Store build"; 2026-10-29: an
   over-the-air update with them hidden, then the build). A shared roster changes who sees a player's plan, so in
   the app it waits for the App Store version that carries it (Xcode "Version" this or later). Below it, and while
   the app's version isn't known yet, the app shows a coach only their own players, as before rosters. On the web,
   always. The next App Store build must be this version or higher. */
export const ROSTERS_MIN_NATIVE_VERSION = "1.1";
export function rostersInApp() {
  return !isNative() || versionAtLeast(nativeAppVersion(), ROSTERS_MIN_NATIVE_VERSION);
}

export function isFeatureOn(key, userEmail) {
  if (FEATURES[key]) return true;
  return isAllowedBetaUser(userEmail);
}

/** Can this coach use direct messages here? Web needs directMessages; the
 *  iPhone app also needs directMessagesNative (see above). */
export function dmsAvailable(email) {
  if (!isFeatureOn("directMessages", email)) return false;
  return !isNative() || versionAtLeast(nativeAppVersion(), DM_MIN_NATIVE_VERSION);
}

/** Can this coach open the coach map here? On the web now; in the iPhone app
 *  from the same reviewed version as DMs. Showing coaches where other coaches
 *  are is privacy-adjacent (CLAUDE.md guardrail B: changing what data goes
 *  where), so in the app it waits for review rather than arriving by OTA. */
export function mapAvailable(email) {
  if (!isFeatureOn("memberMap", email)) return false;
  return !isNative() || versionAtLeast(nativeAppVersion(), DM_MIN_NATIVE_VERSION);
}

/** "1.10" >= "1.9", "1.1" >= "1.1". Unknown (null) is never enough. */
export function versionAtLeast(have, need) {
  if (!have) return false;
  const a = String(have).split(".").map(n => parseInt(n, 10) || 0);
  const b = String(need).split(".").map(n => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    if ((a[i] || 0) !== (b[i] || 0)) return (a[i] || 0) > (b[i] || 0);
  }
  return true;
}
